import {
  RefreshCw,
  Target,
  Brain,
  CheckCircle,
  Users,
  MessageCircle,
  BookOpen,
  Link,
  List,
} from "lucide-react";
import { tC } from "../../utils/TranslateWithContentLanguage";
import CardImage from "../tool_components/CardImage";
import { Button } from "@core/index";
import ProjectBasedLearningPDF from "./ProjectBasedLearningCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { ProjectBasedLearningResponseDto } from "@packages/api/madlenAPI.schemas";
import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetProjectBasedLearningQueryKey,
  getGetUserProjectBasedLearningsQueryKey,
  updateProjectBasedLearning,
} from "@packages/api/madlenAPI";

interface ProjectBasedLearningCardProps {
  project: ProjectBasedLearningResponseDto;
  onCreateAgain?: () => void;
  isEditable?: boolean;
}

const ProjectBasedLearningCard = ({
  project,
  onCreateAgain,
  isEditable = false,
}: ProjectBasedLearningCardProps) => {
  const finalImageUrl = useImagePdf(project.imageUrl);
  const contentLanguage = project.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();

  const renderAssessment = () => {
    if (!project.assessment) return null;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
            <CheckCircle size={20} />
          </div>
          <h4 className="font-semibold text-red-600 dark:text-red-400 text-lg">
            {tC(contentLanguage, "projectBasedLearning.assessment")}
          </h4>
        </div>

        <div className="space-y-4">
          {project.assessment.criteria?.length &&
            project.assessment.criteria.length > 0 && (
              <div>
                <h5 className="font-medium mb-3 text-gray-700 dark:text-gray-300">
                  {tC(
                    contentLanguage,
                    "projectBasedLearning.assessmentCriteria",
                  )}
                  :
                </h5>
                <div className="space-y-2">
                  {project.assessment.criteria.map(
                    (criterion: string, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                        <div className="flex-1">
                          <EditableMarkdown
                            value={criterion}
                            isEditable={isEditable ?? true}
                            handleSave={async (newValue) => {
                              if (!project.assessment?.criteria) return;

                              const updatedCriteria = [
                                ...project.assessment.criteria,
                              ];
                              updatedCriteria[idx] = newValue;

                              await updateProjectBasedLearning(project.id, {
                                assessment: {
                                  ...project.assessment,
                                  criteria: updatedCriteria,
                                },
                              });

                              await queryClient.invalidateQueries({
                                queryKey:
                                  getGetUserProjectBasedLearningsQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetProjectBasedLearningQueryKey(
                                  project.id,
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

          {project.assessment.rubric?.some(
            (item) => item?.criterion && item?.levels,
          ) && (
            <div>
              <h5 className="font-medium mb-3 text-gray-700 dark:text-gray-300">
                {tC(contentLanguage, "projectBasedLearning.assessmentRubric")}:
              </h5>
              <div className="space-y-4">
                {project.assessment.rubric
                  .filter((item) => item?.criterion && item?.levels)
                  .map((item: any, idx: number) => (
                    <div
                      key={idx}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
                    >
                      <h6 className="font-medium mb-2 text-gray-800 dark:text-gray-200">
                        <EditableMarkdown
                          value={item.criterion}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!project.assessment?.rubric) return;

                            const updatedRubric = [
                              ...project.assessment.rubric,
                            ];
                            updatedRubric[idx] = {
                              ...updatedRubric[idx],
                              criterion: newValue,
                            };

                            await updateProjectBasedLearning(project.id, {
                              assessment: {
                                ...project.assessment,
                                rubric: updatedRubric,
                              },
                            });

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserProjectBasedLearningsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetProjectBasedLearningQueryKey(
                                project.id,
                              ),
                            });
                          }}
                        />
                      </h6>
                      <div className="grid gap-2">
                        {item.levels?.map((level: string, levelIdx: number) => (
                          <div
                            key={levelIdx}
                            className="flex items-start gap-2"
                          >
                            <div className="w-1.5 h-1.5 bg-red-300 rounded-full mt-2 flex-shrink-0"></div>
                            <div className="flex-1">
                              <EditableMarkdown
                                value={level}
                                isEditable={isEditable ?? true}
                                handleSave={async (newValue) => {
                                  if (!project.assessment?.rubric) return;

                                  const updatedRubric = [
                                    ...project.assessment.rubric,
                                  ];
                                  const updatedLevels = [
                                    ...updatedRubric[idx].levels,
                                  ];
                                  updatedLevels[levelIdx] = newValue;

                                  updatedRubric[idx] = {
                                    ...updatedRubric[idx],
                                    levels: updatedLevels,
                                  };

                                  await updateProjectBasedLearning(project.id, {
                                    assessment: {
                                      ...project.assessment,
                                      rubric: updatedRubric,
                                    },
                                  });

                                  await queryClient.invalidateQueries({
                                    queryKey:
                                      getGetUserProjectBasedLearningsQueryKey(),
                                  });
                                  await queryClient.invalidateQueries({
                                    queryKey:
                                      getGetProjectBasedLearningQueryKey(
                                        project.id,
                                      ),
                                  });
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPeerSelfAssessment = () => {
    if (!project.peerSelfAssessment) return null;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
            <Users size={20} />
          </div>
          <h4 className="font-semibold text-purple-600 dark:text-purple-400 text-lg">
            {tC(contentLanguage, "projectBasedLearning.peerSelfAssessment")}
          </h4>
        </div>

        <div className="space-y-6">
          {project.peerSelfAssessment.peer_review_questions?.length &&
            project.peerSelfAssessment.peer_review_questions.length > 0 && (
              <div>
                <h5 className="font-medium mb-3 text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <MessageCircle size={16} />
                  {tC(
                    contentLanguage,
                    "projectBasedLearning.peerReviewQuestions",
                  )}
                  :
                </h5>
                <div className="space-y-2">
                  {project.peerSelfAssessment.peer_review_questions.map(
                    (question: string, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg"
                      >
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                        <div className="flex-1">
                          <EditableMarkdown
                            value={question}
                            isEditable={isEditable ?? true}
                            handleSave={async (newValue) => {
                              if (
                                !project.peerSelfAssessment
                                  ?.peer_review_questions
                              )
                                return;

                              const updatedQuestions = [
                                ...project.peerSelfAssessment
                                  .peer_review_questions,
                              ];
                              updatedQuestions[idx] = newValue;

                              await updateProjectBasedLearning(project.id, {
                                peerSelfAssessment: {
                                  ...project.peerSelfAssessment,
                                  peer_review_questions: updatedQuestions,
                                },
                              });

                              await queryClient.invalidateQueries({
                                queryKey:
                                  getGetUserProjectBasedLearningsQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetProjectBasedLearningQueryKey(
                                  project.id,
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

          {project.peerSelfAssessment.self_reflection_questions?.length &&
            project.peerSelfAssessment.self_reflection_questions.length > 0 && (
              <div>
                <h5 className="font-medium mb-3 text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Brain size={16} />
                  {tC(
                    contentLanguage,
                    "projectBasedLearning.selfReflectionQuestions",
                  )}
                  :
                </h5>
                <div className="space-y-2">
                  {project.peerSelfAssessment.self_reflection_questions.map(
                    (question: string, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg"
                      >
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                        <div className="flex-1">
                          <EditableMarkdown
                            value={question}
                            isEditable={isEditable ?? true}
                            handleSave={async (newValue) => {
                              if (
                                !project.peerSelfAssessment
                                  ?.self_reflection_questions
                              )
                                return;

                              const updatedQuestions = [
                                ...project.peerSelfAssessment
                                  .self_reflection_questions,
                              ];
                              updatedQuestions[idx] = newValue;

                              await updateProjectBasedLearning(project.id, {
                                peerSelfAssessment: {
                                  ...project.peerSelfAssessment,
                                  self_reflection_questions: updatedQuestions,
                                },
                              });

                              await queryClient.invalidateQueries({
                                queryKey:
                                  getGetUserProjectBasedLearningsQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetProjectBasedLearningQueryKey(
                                  project.id,
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="project-based-learning-card" className="min-h-screen">
      <div className="container max-w-7xl mx-auto px-4 py-4">
        {project.imageUrl && (
          <CardImage
            createdAt={project.createdAt}
            imageUrl={project.imageUrl}
            videoUrl={project.videoUrl}
            courseName={project.courseName}
            gradeLevel={project.gradeLevel}
            topic={project.title}
            theme="red"
            contentLanguage={contentLanguage}
          />
        )}
        <div className="flex justify-end mb-3 gap-2 mt-2">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-red-400 to-pink-500 dark:from-red-300 dark:to-pink-400 hover:from-red-500 hover:to-pink-600 dark:hover:from-red-400 dark:hover:to-pink-500"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}

          <PdfDownloadButton
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            pdfTitle={project.title || ""}
            document={
              <ProjectBasedLearningPDF
                project={{ ...project, imageUrl: finalImageUrl }}
                customizationSettings={customizationSettings}
              />
            }
            btnTheme="bg-gradient-to-r from-red-400 to-pink-500 dark:from-red-300 dark:to-pink-400"
          />
        </div>

        <div className="space-y-8">
          <div className="bg-white/80 dark:bg-red-900/20 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-red-200/50 dark:border-red-700/50">
            <div className="space-y-8">
              {/* Description Section */}
              {project.description && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                      <BookOpen size={20} />
                    </div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400 text-lg">
                      {tC(contentLanguage, "projectBasedLearning.description")}
                    </h4>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <EditableMarkdown
                      value={project.description}
                      isEditable={isEditable ?? true}
                      handleSave={async (newValue) => {
                        await updateProjectBasedLearning(project.id, {
                          description: newValue,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserProjectBasedLearningsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetProjectBasedLearningQueryKey(
                            project.id,
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Project Problem Section */}
              {project.projectProblem?.questions?.length &&
                project.projectProblem.questions.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
                        <Target size={20} />
                      </div>
                      <h4 className="font-semibold text-red-600 dark:text-red-400 text-lg">
                        {tC(
                          contentLanguage,
                          "projectBasedLearning.projectProblem",
                        )}
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {project.projectProblem.questions.map((question, idx) => (
                        <div
                          key={idx}
                          className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-400"
                        >
                          <EditableMarkdown
                            value={question}
                            isEditable={isEditable ?? true}
                            handleSave={async (newValue) => {
                              if (!project.projectProblem?.questions) return;

                              const updatedQuestions = [
                                ...project.projectProblem.questions,
                              ];
                              updatedQuestions[idx] = newValue;

                              await updateProjectBasedLearning(project.id, {
                                projectProblem: {
                                  ...project.projectProblem,
                                  questions: updatedQuestions,
                                },
                              });

                              await queryClient.invalidateQueries({
                                queryKey:
                                  getGetUserProjectBasedLearningsQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetProjectBasedLearningQueryKey(
                                  project.id,
                                ),
                              });
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Real Life Connection Section */}
              {project.realLifeConnection && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                      <Link size={20} />
                    </div>
                    <h4 className="font-semibold text-green-600 dark:text-green-400 text-lg">
                      {tC(
                        contentLanguage,
                        "projectBasedLearning.realLifeConnection",
                      )}
                    </h4>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <EditableMarkdown
                      value={project.realLifeConnection}
                      isEditable={isEditable ?? true}
                      handleSave={async (newValue) => {
                        await updateProjectBasedLearning(project.id, {
                          realLifeConnection: newValue,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserProjectBasedLearningsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetProjectBasedLearningQueryKey(
                            project.id,
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Process Steps Section */}
              {project.processSteps && project.processSteps.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
                      <List size={20} />
                    </div>
                    <h4 className="font-semibold text-purple-600 dark:text-purple-400 text-lg">
                      {tC(contentLanguage, "projectBasedLearning.processSteps")}
                    </h4>
                  </div>
                  <div className="space-y-4">
                    {project.processSteps.map((step, idx) => (
                      <div key={idx} className="relative">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center font-semibold text-sm">
                            {idx + 1}
                          </div>
                          <div className="flex-1 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                            <EditableMarkdown
                              value={step}
                              isEditable={isEditable ?? true}
                              handleSave={async (newValue) => {
                                if (!project.processSteps) return;

                                const updatedSteps = [...project.processSteps];
                                updatedSteps[idx] = newValue;

                                await updateProjectBasedLearning(project.id, {
                                  processSteps: updatedSteps,
                                });

                                await queryClient.invalidateQueries({
                                  queryKey:
                                    getGetUserProjectBasedLearningsQueryKey(),
                                });
                                await queryClient.invalidateQueries({
                                  queryKey: getGetProjectBasedLearningQueryKey(
                                    project.id,
                                  ),
                                });
                              }}
                            />
                          </div>
                        </div>
                        {idx < project.processSteps.length - 1 && (
                          <div className="absolute left-4 top-8 w-0.5 h-6 bg-purple-200 dark:bg-purple-700"></div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback Points Section */}
              {project.feedbackPoints && project.feedbackPoints.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400">
                      <MessageCircle size={20} />
                    </div>
                    <h4 className="font-semibold text-amber-600 dark:text-amber-400 text-lg">
                      {tC(
                        contentLanguage,
                        "projectBasedLearning.feedbackPoints",
                      )}
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {project.feedbackPoints.map((point, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg"
                      >
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <div className="flex-1">
                          <EditableMarkdown
                            value={point}
                            isEditable={isEditable ?? true}
                            handleSave={async (newValue) => {
                              if (!project.feedbackPoints) return;

                              const updatedPoints = [...project.feedbackPoints];
                              updatedPoints[idx] = newValue;

                              await updateProjectBasedLearning(project.id, {
                                feedbackPoints: updatedPoints,
                              });

                              await queryClient.invalidateQueries({
                                queryKey:
                                  getGetUserProjectBasedLearningsQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetProjectBasedLearningQueryKey(
                                  project.id,
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assessment and Peer/Self Assessment */}
              {renderAssessment()}
              {renderPeerSelfAssessment()}
            </div>
          </div>
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <ProjectBasedLearningPDF
            project={{ ...project, imageUrl: finalImageUrl }}
            customizationSettings={customizationSettings}
          />
        }
      />
    </div>
  );
};

export default ProjectBasedLearningCard;
